import { createContext, useContext, useCallback, useState, useEffect, useMemo, useRef, type ReactNode } from "react";

type OnboardingStep =
  | "welcome"
  | "profile_setup"
  | "enable_location"
  | "first_search"
  | "first_favorite"
  | "first_message"
  | "first_listing"
  | "verify_identity"
  | "completed";

interface OnboardingState {
  currentStep: OnboardingStep;
  completedSteps: OnboardingStep[];
  skippedSteps: OnboardingStep[];
  showTooltips: boolean;
  isComplete: boolean;
}

// Split into stable actions and changing state
interface OnboardingActionsContextType {
  completeStep: (step: OnboardingStep) => void;
  skipStep: (step: OnboardingStep) => void;
  resetOnboarding: () => void;
  setShowTooltips: (show: boolean) => void;
  goToStep: (step: OnboardingStep) => void;
}

interface OnboardingStateContextType extends OnboardingState {
  isStepComplete: (step: OnboardingStep) => boolean;
  isStepSkipped: (step: OnboardingStep) => boolean;
}

const STORAGE_KEY = "listo_onboarding";

const STEP_ORDER: OnboardingStep[] = [
  "welcome",
  "profile_setup",
  "enable_location",
  "first_search",
  "first_favorite",
  "first_message",
  "first_listing",
  "verify_identity",
  "completed",
];

const defaultState: OnboardingState = {
  currentStep: "welcome",
  completedSteps: [],
  skippedSteps: [],
  showTooltips: true,
  isComplete: false,
};

const OnboardingActionsContext = createContext<OnboardingActionsContextType | null>(null);
const OnboardingStateContext = createContext<OnboardingStateContextType | null>(null);

interface OnboardingProviderProps {
  children: ReactNode;
}

// Helper to get next incomplete step
function getNextStep(completedSteps: OnboardingStep[], skippedSteps: OnboardingStep[]): OnboardingStep {
  const handled = new Set([...completedSteps, ...skippedSteps]);
  for (const step of STEP_ORDER) {
    if (!handled.has(step)) {
      return step;
    }
  }
  return "completed";
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const [state, setState] = useState<OnboardingState>(() => {
    if (typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          return { ...defaultState, ...JSON.parse(stored) };
        }
      } catch {
        // Ignore parse errors
      }
    }
    return defaultState;
  });

  // Debounce storage writes
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    saveTimeoutRef.current = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }, 100);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [state]);

  // Memoize actions - they don't depend on state directly
  const actions = useMemo<OnboardingActionsContextType>(() => ({
    completeStep: (step: OnboardingStep) => {
      setState((prev) => {
        if (prev.completedSteps.includes(step)) return prev;
        const completedSteps = [...prev.completedSteps, step];
        const currentStep = getNextStep(completedSteps, prev.skippedSteps);
        return {
          ...prev,
          completedSteps,
          currentStep,
          isComplete: currentStep === "completed",
        };
      });
    },
    skipStep: (step: OnboardingStep) => {
      setState((prev) => {
        if (prev.skippedSteps.includes(step) || prev.completedSteps.includes(step)) {
          return prev;
        }
        const skippedSteps = [...prev.skippedSteps, step];
        const currentStep = getNextStep(prev.completedSteps, skippedSteps);
        return {
          ...prev,
          skippedSteps,
          currentStep,
          isComplete: currentStep === "completed",
        };
      });
    },
    resetOnboarding: () => {
      setState(defaultState);
      localStorage.removeItem(STORAGE_KEY);
    },
    setShowTooltips: (show: boolean) => {
      setState((prev) => (prev.showTooltips === show ? prev : { ...prev, showTooltips: show }));
    },
    goToStep: (step: OnboardingStep) => {
      setState((prev) => (prev.currentStep === step ? prev : { ...prev, currentStep: step }));
    },
  }), []);

  // Memoize step check functions using Sets for O(1) lookup
  const completedSet = useMemo(() => new Set(state.completedSteps), [state.completedSteps]);
  const skippedSet = useMemo(() => new Set(state.skippedSteps), [state.skippedSteps]);

  const stateValue = useMemo<OnboardingStateContextType>(() => ({
    ...state,
    isStepComplete: (step: OnboardingStep) => completedSet.has(step),
    isStepSkipped: (step: OnboardingStep) => skippedSet.has(step),
  }), [state, completedSet, skippedSet]);

  return (
    <OnboardingActionsContext.Provider value={actions}>
      <OnboardingStateContext.Provider value={stateValue}>
        {children}
      </OnboardingStateContext.Provider>
    </OnboardingActionsContext.Provider>
  );
}

// Stable actions hook
export function useOnboardingActions() {
  const context = useContext(OnboardingActionsContext);
  if (!context) {
    throw new Error("useOnboardingActions must be used within an OnboardingProvider");
  }
  return context;
}

// State hook
export function useOnboardingState() {
  const context = useContext(OnboardingStateContext);
  if (!context) {
    throw new Error("useOnboardingState must be used within an OnboardingProvider");
  }
  return context;
}

// Selector hooks
export function useCurrentStep(): OnboardingStep {
  return useOnboardingState().currentStep;
}

export function useIsOnboardingComplete(): boolean {
  return useOnboardingState().isComplete;
}

export function useShowTooltips(): boolean {
  return useOnboardingState().showTooltips;
}

// Combined hook for backwards compatibility
export function useOnboarding() {
  const state = useOnboardingState();
  const actions = useOnboardingActions();
  return { ...state, ...actions };
}

// Helper hook to track and complete steps automatically
export function useOnboardingStep(step: OnboardingStep, autoComplete = false) {
  const { completeStep } = useOnboardingActions();
  const { currentStep, isStepComplete } = useOnboardingState();
  
  const isActive = currentStep === step;
  const isCompleted = isStepComplete(step);

  useEffect(() => {
    if (autoComplete && isActive && !isCompleted) {
      completeStep(step);
    }
  }, [autoComplete, isActive, isCompleted, step, completeStep]);

  return useMemo(
    () => ({ isActive, isCompleted, complete: () => completeStep(step) }),
    [isActive, isCompleted, completeStep, step]
  );
}

export type { OnboardingStep, OnboardingState };
